import type { MigrationManifest, PersistedState } from "redux-persist";
import {
  createMigrate,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from "redux-persist";

import { combineReducers, configureStore } from "@reduxjs/toolkit";

import localforage from "localforage";

import { MIGRATION_KEYS } from "./migrations/constant";
import { analysisJobReducer, resetAnalysisJob } from "./slice/analysisJob";
import {
  analysisSummaryReducer,
  resetAnalysisSummaryState
} from "./slice/analysisSummary";
import { authReducer, resetAuth } from "./slice/authSlice";
import type { DatasetState } from "./slice/datasetSlice";
import { datasetReducer, resetDatasetState } from "./slice/datasetSlice";
import {
  globalFilterReducer,
  GlobalFilterState,
  resetGlobalFilter
} from "./slice/globalFilterSlice";
import { mapReducer, resetMapState } from "./slice/mapSlice";
import { resetSpatialState, spatialReducer } from "./slice/spatialSlice";
import { resetUIDataConfig, uiReducer } from "./slice/uiSlice";
import { resetWidgetState, widgetReducer } from "./slice/widgetSlice";

localforage.config({
  name: "lokasi-intelligence",
  storeName: "lokasi_intelligence_store"
});

type MigrationManifestTyped<S> = {
  [version: number]: (
    state: (S & PersistedState) | undefined
  ) => S | undefined | Promise<S | undefined>;
};

type CreatePersistConfigParams<S> = {
  key: string;
  version: number;
  storage: LocalForage;
  migrations?: MigrationManifestTyped<S>;
  whitelist?: string[];
  blacklist?: string[];
};

export const createPersistConfig = <S>({
  key,
  version,
  storage,
  migrations = {},
  whitelist,
  blacklist
}: CreatePersistConfigParams<S>) => {
  const baseMigrate = createMigrate(migrations as MigrationManifest, {
    debug: import.meta.env.MODE === "development"
  });

  const migrate = async (state: PersistedState, currentVersion: number) => {
    const fromVersion = state?._persist?.version ?? 0;

    if (fromVersion >= currentVersion) {
      return state;
    }

    return baseMigrate(state, currentVersion);
  };

  return {
    key,
    version,
    storage,
    whitelist,
    blacklist,
    migrate: Object.keys(migrations).length ? migrate : undefined
  };
};

const mapPersistConfig = createPersistConfig({
  key: "map",
  version: 0,
  storage: localforage,
  blacklist: ["selectedFeature"]
});

const spatialPersistConfig = createPersistConfig({
  key: "spatial",
  version: 0,
  storage: localforage,
  blacklist: [
    "catchmentSettingsTemp",
    "adminAreaSettingsDataTemp",
    "adminTreeSettingsTemp"
  ]
});

const datasetPersistConfig = createPersistConfig<DatasetState>({
  key: "dataset",
  version: 0,
  storage: localforage
});

const analysisSummaryPersistConfig = createPersistConfig({
  key: "analysisSummary",
  version: 0,
  storage: localforage,
  blacklist: ["gridsSelected"]
});

const uiPersistConfig = createPersistConfig({
  key: "ui",
  version: 0,
  storage: localforage,
  blacklist: [
    "showPolygonPopup",
    "allowPinMarker",
    "showAreaAdminPopup",
    "showCatchmentPopup",
    "showDatasetPopup",
    "showDataWeightSettingsPopup",
    "showSpatialAnalysisResultTabularModal",
    "sideBarMenuTypePopupDisplay",
    "isExpandLegendVisualization",
    "showProjectList",
    "showLegendInfo",
    "sidebarUserSettingPopupDisplay",
    "showSearchLocationPopup",
    "markerInfo",
    "showGlobalFilterPopup",
    "showLoadingOverlay"
  ]
});

const authPersistConfig = createPersistConfig({
  key: "auth",
  version: 0,
  storage: localforage
});

const analysisJobPersistConfig = createPersistConfig({
  key: "analysisJob",
  version: 0,
  storage: localforage
});

const globalFilterPersistConfig = createPersistConfig({
  key: "globalFilter",
  version: 1,
  storage: localforage,
  blacklist: ["adminAreaSettingsDataTemp", "selectedAreaIdsTemp"],
  migrations: {
    /**
     * Version 1: Remove LinkedDatasetID from globalFilter state as it is no longer needed after the dataset state has been migrated to remove linkedDatasetID.
     * This migration will ensure that any existing persisted state that still contains linkedDatasetID will have it removed.
     * The linkedDatasetID will be temporarily stored in localforage during the migration process and will be used in the App component to update the dataset state accordingly before being removed from localforage.
     */
    1: async (state) => {
      const legacyState = state as
        | (GlobalFilterState & { linkedDatasetIds?: string[] })
        | undefined;

      if (!legacyState) return state;

      try {
        if (legacyState.linkedDatasetIds?.length) {
          await localforage.setItem(
            MIGRATION_KEYS.LINKED_DATASET_IDS,
            legacyState.linkedDatasetIds
          );
        }
      } catch (e) {
        console.error(
          "[globalFilter migration v1] Failed to save linkedDatasetIds:",
          e
        );
      }

      delete legacyState.linkedDatasetIds;
      return legacyState;
    }
  }
});

const widgetPersisConfig = createPersistConfig({
  key: "widget",
  version: 0,
  storage: localforage
});

export const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  map: persistReducer(mapPersistConfig, mapReducer),
  dataset: persistReducer(datasetPersistConfig, datasetReducer),
  spatial: persistReducer(spatialPersistConfig, spatialReducer),
  ui: persistReducer(uiPersistConfig, uiReducer),
  analysisSummary: persistReducer(
    analysisSummaryPersistConfig,
    analysisSummaryReducer
  ),
  analysisJob: persistReducer(analysisJobPersistConfig, analysisJobReducer),
  globalFilter: persistReducer(globalFilterPersistConfig, globalFilterReducer),
  widget: persistReducer(widgetPersisConfig, widgetReducer)
});

export const store = configureStore({
  devTools: import.meta.env.MODE !== "production",
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Actions from redux-persist are ignored because they may contain non-serializable values
        // and are essential for the persistence mechanism to work correctly.
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    })
});

export const persistor = persistStore(store);

// Function to reset the store
// We can custom the store we want to reset
export const resetStore = async () => {
  store.dispatch(resetAnalysisSummaryState());
  store.dispatch(resetAuth());
  store.dispatch(resetDatasetState());
  store.dispatch(resetMapState());
  store.dispatch(resetSpatialState());
  store.dispatch(resetUIDataConfig());
  store.dispatch(resetAnalysisJob());
  store.dispatch(resetGlobalFilter());
  store.dispatch(resetWidgetState());
};

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
